import { Protocol, Signature } from "../../entities";
import { isAmendment, isDeleteChange, isReplaceChange, } from "../types";
import logger from "../../logger";
import { AnchoringAweighParser, AnchoringHeavingUpParser, AnchoringLetGoParser, AnchoringOutOfTheWaterParser, AnchoringRoundParser, AnchoringSecuredParser, AnchoringVesselAtAnchorParser, } from './anchoring';
import ApprovalParser from './ApprovalParser';
import BeginOrEndOfSeaPassageParser from './atSea/BeginOrEndOfSeaPassageParser';
import BridgeNavigationalWatchAlarmSystemParser from './atSea/BridgeNavigationalWatchAlarmSystemParser';
import SafetyAndSecurityRoundParser from './atSea/SafetyAndSecurityRoundParser';
import StressAndStabilityAtSeaParser from './atSea/StressAndStabilityAtSeaParser';
import BookKeyBundleParser from './BookKeyBundleParser';
import { BunkerBargeAlongsideParser, BunkerBargeOffParser } from './bunkerbarge';
import ChecklistParser from './ChecklistParser';
import CrewListParser from './CrewListParser';
import FreeEntryParser from './FreeEntryParser';
import GatewayParsingError from './GatewayParsingError';
import GatewayRecordParser from './GatewayRecordParser';
import GenericRecordParser from './GenericRecordParser';
import PortOperationsParser from './inPort/PortOperationsParser';
import SafetyAndSecurityRoundInPortParser from './inPort/SafetyAndSecurityRoundInPortParser';
import StressAndStabilityInPortParser from './inPort/StressAndStabilityInPortParser';
import { MooringArrivalCheckListParser, MooringArrivalParser, MooringDepartureCheckListParser, MooringDepartureParser, } from './mooring';
import MooringAfterArrivalParser from './mooring/MooringAfterArrivalParser';
import MooringArrivalLineHandlingParser from './mooring/MooringArrivalLineHandlingParser';
import MooringBeforeDepartureParser from './mooring/MooringBeforeDepartureParser';
import MooringDepartureLineHandlingParser from './mooring/MooringDepartureLineHandlingParser';
import { MooringApproachingParser, MooringSailingOffParser } from './mooring/MooringPassingMarkParser';
import { MooringArrivalSwingingParser, MooringDepartureSwingingParser } from './mooring/MooringSwingingParser';
import NavigationManualInputParser from './navigation/NavigationManualInputParser';
import NavigationParser from './navigation/NavigationParser';
import NoonReportParser from './NoonReportParser';
import { getAuthorRefForGateway, getAuthorRefFromGateway } from './ParsingUtils';
import { PilotOnBoardParser, PilotReleasedParser } from './pilot';
import RecordFileParser from './RecordFileParser';
import RouteMonitoringParser from './routeMonitoring/RouteMonitoringParser';
import SoundingsParser from './SoundingsParser';
import TimeZoneParser from './TimeZoneParser';
import { TugEngagedParser, TugReleasedParser } from './tug';
import VesselParticularsParser from './VesselParticularsParser';
import VoyageParser from './VoyageParser';
import HandOverWatchParser from './watchHandover/HandOverWatchParser';
import TakeOverWatchParser from './watchHandover/TakeOverWatchParser';
import WeatherObservationParser from './WeatherObservationParser';
export function init(app) {
    const genericParser = new GenericRecordParser(app);
    const parsers = [
        new NavigationParser(app),
        new NavigationManualInputParser(app),
        new WeatherObservationParser(app),
        new BunkerBargeAlongsideParser(app),
        new BunkerBargeOffParser(app),
        new TugEngagedParser(app),
        new TugReleasedParser(app),
        new PilotOnBoardParser(app),
        new PilotReleasedParser(app),
        new AnchoringLetGoParser(app),
        new AnchoringVesselAtAnchorParser(app),
        new AnchoringRoundParser(app),
        new AnchoringAweighParser(app),
        new AnchoringHeavingUpParser(app),
        new AnchoringOutOfTheWaterParser(app),
        new AnchoringSecuredParser(app),
        new FreeEntryParser(app),
        new MooringArrivalCheckListParser(app),
        new MooringDepartureCheckListParser(app),
        new MooringArrivalSwingingParser(app),
        new MooringDepartureSwingingParser(app),
        new MooringApproachingParser(app),
        new MooringSailingOffParser(app),
        new MooringBeforeDepartureParser(app),
        new MooringAfterArrivalParser(app),
        new MooringArrivalLineHandlingParser(app),
        new MooringDepartureParser(app),
        new MooringDepartureLineHandlingParser(app),
        new MooringArrivalParser(app),
        new SoundingsParser(app),
        new TakeOverWatchParser(app),
        new HandOverWatchParser(app),
        new TimeZoneParser(app),
        new BeginOrEndOfSeaPassageParser(app),
        new SafetyAndSecurityRoundParser(app),
        new BridgeNavigationalWatchAlarmSystemParser(app),
        new SafetyAndSecurityRoundInPortParser(app),
        new StressAndStabilityInPortParser(app),
        new PortOperationsParser(app),
        new RouteMonitoringParser(app),
        new StressAndStabilityAtSeaParser(app),
        new NoonReportParser(app),
        new ChecklistParser(app),
        new GatewayRecordParser(app),
    ];
    function getParser(protocol) {
        const handlingParsers = parsers.map((parser) => {
            if (parser.handlesProtocol(protocol)) {
                return parser;
            }
            return null;
        });
        const parser = handlingParsers.find((parser) => parser !== null);
        if (!parser) {
            return genericParser;
        }
        return parser;
    }
    function parseDeletedProtocol(protocol, parser) {
        if (protocol.predecessor === undefined) {
            throw new GatewayParsingError('Can only be called for protocols with predecessors');
        }
        const extensions = parser.getExtensions(protocol);
        return {
            Id: protocol.gatewayAmendmentId,
            AmendedBy: getAuthorRefForGateway(protocol.author, app),
            AmendedWhen: protocol.createdTimestamp,
            Changes: [
                {
                    Ref: protocol.predecessor._id,
                    Operation: 'delete',
                    Extensions: [
                        ...extensions,
                        {
                            Manufacturer: 'ANZ',
                            Name: 'Reason',
                            Value: protocol.reasonForChange || '',
                        },
                    ],
                },
            ],
        };
    }
    function parseProtocol(protocol) {
        const parser = getParser(protocol);
        if (protocol.deleted) {
            return parseDeletedProtocol(protocol, parser);
        }
        const record = parser.parseProtocol(protocol);
        if (protocol.predecessor) {
            return {
                Id: protocol.gatewayAmendmentId,
                AmendedBy: record.Author,
                AmendedWhen: protocol.createdTimestamp,
                Changes: [
                    {
                        Ref: protocol.predecessor._id,
                        Operation: 'replace',
                        ReplacementRecord: record,
                        Extensions: [
                            {
                                Manufacturer: 'ANZ',
                                Name: 'Reason',
                                Value: protocol.reasonForChange || '',
                            },
                        ],
                    },
                ],
            };
        }
        return record;
    }
    function getRecordParser(record) {
        const handlingParsers = parsers.map((parser) => {
            if (parser.handlesRecord(record)) {
                return parser;
            }
            return null;
        });
        const parser = handlingParsers.find((parser) => parser !== null);
        if (!parser) {
            return genericParser;
        }
        return parser;
    }
    function getReasonForChange(change) {
        if (change.Extensions) {
            const findResult = change.Extensions.find((e) => e.Name === 'Reason');
            if (findResult !== undefined) {
                return findResult.Value;
            }
        }
        else {
            return undefined;
        }
    }
    function parseAmendment(amendment, parser, timestamp, signature) {
        let signatureObject = undefined;
        if (signature &&
            amendment.SignInfo &&
            amendment.SignInfo.SignatureMethod &&
            amendment.SignInfo.KeyInfo &&
            amendment.SignInfo.KeyInfo.KeyName) {
            signatureObject = new Signature(signature, amendment.SignInfo.KeyInfo.KeyName, amendment.SignInfo.SignatureMethod);
        }
        const change = amendment.Changes[0];
        if (isReplaceChange(change)) {
            const record = change.ReplacementRecord;
            const parsedProtocol = parseRecord(record, timestamp);
            parsedProtocol.reasonForChange = getReasonForChange(change);
            parsedProtocol.signature = signatureObject;
            Object.assign(parsedProtocol, {
                createdTimestamp: amendment.AmendedWhen,
                predecessor: Protocol.createRef(change.Ref),
                gatewayAmendmentId: amendment.Id,
            });
            return parsedProtocol;
        }
        if (isDeleteChange(change)) {
            const protocol = parser.createProtocol(timestamp, getAuthorRefFromGateway(amendment.AmendedBy, app), parser.getTypeRefForProtocol(amendment), [], parser.getBooks(amendment));
            protocol.reasonForChange = getReasonForChange(change);
            protocol.signature = signatureObject;
            Object.assign(protocol, {
                _id: amendment.Id,
                createdTimestamp: amendment.AmendedWhen,
                deleted: true,
                predecessor: Protocol.createRef(change.Ref),
                gatewayAmendmentId: amendment.Id,
            });
            return protocol;
        }
        throw new GatewayParsingError(`Unknown amendment type: ${amendment.Id}`);
    }
    function parseRecord(record, timestamp, signature) {
        const parser = getRecordParser(record);
        try {
            if (isAmendment(record)) {
                return parseAmendment(record, parser, timestamp, signature);
            }
            return parser.parseRecord(record, timestamp, signature);
        }
        catch (error) {
            logger.error('Error at parsing record "%s". Falling back to generic parser. Reason: %s', record.Id, `${error}`);
            if (isAmendment(record)) {
                return parseAmendment(record, genericParser, timestamp, signature);
            }
            return genericParser.parseRecord(record, timestamp, signature);
        }
    }
    app.set('parseProtocol', parseProtocol);
    app.set('parseRecord', parseRecord);
}
export * from './ParsingUtils';
export { ApprovalParser, BookKeyBundleParser, CrewListParser, GatewayParsingError, RecordFileParser, VesselParticularsParser, VoyageParser, };
//# sourceMappingURL=index.js.map